import { useState } from 'react';
import { createPortal } from 'react-dom';
import Button from '../common/Button';
import betslipService from '../../services/betslipService';

const TextParseBetSlipModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [source, setSource] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!text.trim()) {
      setError('Please paste your betslip text');
      return;
    }

    setLoading(true);

    try {
      // Use local date (not UTC) to avoid timezone shifting the date by a day
      const now = new Date();
      const betDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const result = await betslipService.parseTextBetslip(
        text,
        betDate,
        source,
        sourceUrl
      );

      if (onSuccess) {
        onSuccess(result);
      }

      // Reset form
      setText('');
      setSource('');
      setSourceUrl('');

      onClose();
    } catch (error) {
      console.error('Error parsing betslip:', error);
      setError(error.response?.data?.detail || 'Failed to parse betslip. Please try again or enter manually.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-card border border-border rounded-xl p-6 w-full max-w-2xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-text-primary">Paste Bet Slip</h2>
            <p className="text-sm text-text-secondary mt-1">
              Paste text from any sportsbook - we'll parse it automatically
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Bet Slip Text */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Bet Slip Text *
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Paste your bet slip here...

Example:
Chiefs -3.5 (-110)
Lakers vs Celtics OVER 225.5 (-110)
LeBron James Over 25.5 Points (-115)

Total Odds: +450
Stake: $10.00
Potential Win: $55.00`}
              rows={10}
              className="w-full px-4 py-3 bg-bg-primary border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-blue resize-none font-mono text-sm"
              required
            />
            <p className="text-xs text-text-secondary mt-2">
              Supports: DraftKings, FanDuel, BetMGM, Caesars, and most sportsbook formats
            </p>
          </div>

          {/* Source/Handicapper */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Source / Handicapper
              </label>
              <input
                type="text"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="e.g., @SharpPlays, ESPN"
                className="w-full px-4 py-2 bg-bg-card border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-blue"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Source URL
              </label>
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://twitter.com/..."
                className="w-full px-4 py-2 bg-bg-card border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-blue"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-accent-red/10 border border-accent-red/20 rounded-lg">
              <p className="text-sm text-accent-red">{error}</p>
            </div>
          )}

          {/* Tips */}
          <div className="glass-card p-4 border border-border">
            <h4 className="text-sm font-medium text-text-primary mb-2">Tips for best results:</h4>
            <ul className="text-xs text-text-secondary space-y-1">
              <li>• Copy the entire bet slip including odds and stake</li>
              <li>• Include team names, lines, and odds for each bet</li>
              <li>• For player props, include player name and stat type</li>
              <li>• Our AI will automatically detect the sportsbook and bet type</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={loading || !text.trim()}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Parsing...
                </span>
              ) : (
                'Parse & Create'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TextParseBetSlipModal;
